import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { designTokens } from '@monorepo/nova-ui';
import { QuickPurchaseError } from '../hooks/use-quick-purchase-flow';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.customShape.borderRadius4XL,
    border: `2px solid ${theme.palette.error.main}`,
    padding: theme.spacing(12),
    width: '100%',
    maxWidth: theme.spacing(130),
    boxShadow: '0px 144px 250px 0px rgba(221, 253, 108, 0.2)',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(12, 6),
      maxWidth: theme.spacing(80),
    },
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'transparent',
  },
}));

const StyledDialogContent = styled(DialogContent)({
  padding: 0,
});

const StyledTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.h3,
  fontWeight: designTokens.typography.fontWeights.bold,
  textAlign: 'center',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    ...theme.typography.h5,
    fontWeight: designTokens.typography.fontWeights.bold,
  },
}));

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  textAlign: 'center',
  color: theme.palette.text.primary,
  [theme.breakpoints.down('sm')]: {
    ...theme.typography.body2,
  },
}));

const StyledContent = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(6),
}));

interface QuickPurchaseErrorModalProps {
  error: QuickPurchaseError | null;
}

const ERROR_TRANSLATIONS = {
  [QuickPurchaseError.ProgramNotFound]: {
    titleKey: 'error_modal.title_program_not_found',
    subtitleKey: 'error_modal.subtitle_program_not_found',
  },
  [QuickPurchaseError.ProgramNotSellable]: {
    titleKey: 'error_modal.title_program_unavailable',
    subtitleKey: 'error_modal.subtitle_program_unavailable',
  },
  [QuickPurchaseError.AssetResolutionFailed]: {
    titleKey: 'error_modal.title_invalid_program',
    subtitleKey: 'error_modal.subtitle_invalid_program',
  },
  [QuickPurchaseError.CartFailed]: {
    titleKey: 'error_modal.title_cart_failed',
    subtitleKey: 'error_modal.subtitle_cart_failed',
  },
  [QuickPurchaseError.CartPurchaseLimit]: {
    titleKey: 'error_modal.title_cart_purchase_limit',
    subtitleKey: 'error_modal.subtitle_cart_purchase_limit',
  },
  [QuickPurchaseError.CartCountryExcluded]: {
    titleKey: 'error_modal.title_cart_country_excluded',
    subtitleKey: 'error_modal.subtitle_cart_country_excluded',
  },
  [QuickPurchaseError.ExistingUserMissingProfile]: {
    titleKey: 'error_modal.title_existing_user_missing_profile',
    subtitleKey: 'error_modal.subtitle_existing_user_missing_profile',
  },
  [QuickPurchaseError.OtpEmailMismatch]: {
    titleKey: 'error_modal.title_generic',
    subtitleKey: 'error_modal.subtitle_generic',
  },
  [QuickPurchaseError.Generic]: {
    titleKey: 'error_modal.title_generic',
    subtitleKey: 'error_modal.subtitle_generic',
  },
} as const;

export const QuickPurchaseErrorModal: FC<QuickPurchaseErrorModalProps> = ({
  error,
}) => {
  const { t } = useTranslation(['quick-purchase']);

  const { titleKey, subtitleKey } =
    ERROR_TRANSLATIONS[error ?? QuickPurchaseError.Generic] ??
    ERROR_TRANSLATIONS[QuickPurchaseError.Generic];

  return (
    <StyledDialog open disableEscapeKeyDown>
      <StyledDialogContent>
        <StyledContent>
          <StyledTitle>{t(titleKey)}</StyledTitle>
          <StyledSubtitle>{t(subtitleKey)}</StyledSubtitle>
        </StyledContent>
      </StyledDialogContent>
    </StyledDialog>
  );
};
